import { BottomSheetDragHandle } from 'components/bottomsheet/components/BottomSheetDragHandle';
import DragBar from 'components/bottomsheet/components/DragBar';
import ScrollBar from 'components/bottomsheet/components/ScrollBar';
import { AnimatePresence, LazyMotion, m } from 'framer-motion';
import useBottomSheet from 'hooks/common/useBottomSheet';
import { useBottomSheetDrag } from 'hooks/common/useBottomSheetDrag';
import useTouchAction from 'hooks/common/useTouchAction';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BottomSheetProps } from 'types/bottomsheet';
import MyCoinBadge from './CoinBadge';

const loadFeatures = () => import('types/motionFeature').then((res) => res.default);

export const DRAG_BAR_HEIGHT_REM = 2.5;
const DRAG_HANDLE_HEIGHT = 100;
const DRAG_THRESHOLD = 6;
const TRANSITION_DURATION = 0.3;
const TRANSITION_DURATION_MS = TRANSITION_DURATION * 1000;

export const BottomSheet: React.FC<BottomSheetProps> = ({ id, children, sheet }) => {
  const { y, status } = sheet;
  const { close, setStatus } = useBottomSheet();

  const sheetRef = useRef<HTMLDivElement>(null); // 스크롤 컨테이너
  const [sheetHeight, setSheetHeight] = useState(0);
  const { fullScreen, background, draggable, scrollable, blur, containerStyle, zIndex, maxHeight, showScrollBar, showMyCoin, hideBar, disableAnimation, backdropColor } = sheet.options;
  const isRTL = BResource.isRTL();

  const { controls, onDragStart, onDrag, onDragEnd } = useBottomSheetDrag({ y, onClose: () => close(id) });

  const onTouchAction = (overscrollDistance: number, touchEnd: boolean) => {
    y.set(overscrollDistance);
    if (overscrollDistance > 56 && touchEnd) close(id);
  };

  // 스크롤 최상단에서 드래그 동작으로 넘어가게 하기 위한 훅
  useTouchAction({ containerRef: sheetRef, onTouchAction, scrollable });

  // sheet 높이 변화 감지
  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      const totalHeight = rect.height;
      setSheetHeight(totalHeight);
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const portalRoot = useMemo(() => sheet.mountTo ?? (typeof document !== 'undefined' ? document.getElementById('hMainContainer') : null), [sheet.mountTo]);
  if (!portalRoot) return null;

  return createPortal(
    <AnimatePresence>
      <LazyMotion features={loadFeatures} strict>
        <div data-component="BottomSheet" className="pointer-events-none fixed inset-0 z-[103]" style={{ direction: isRTL ? 'rtl' : 'ltr', zIndex }}>
          {/*backdrop */}
          <m.div
            id="bottom-sheet-backdrop"
            onClick={() => {
              if (status === 'processing') return;

              close(id);
            }}
            className="pointer-events-auto fixed inset-0"
            initial={disableAnimation ? { opacity: 0.4 } : { opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            transition={{ duration: TRANSITION_DURATION, ease: 'easeInOut' }}
            style={{ backgroundColor: backdropColor || 'rgba(0, 0, 0, 1)' }}
          />
          {/* sheet */}
          <m.div
            data-bottom-sheet-id={id}
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            className={`scroll-container no-scrollbar pointer-events-auto fixed bottom-0 left-0 right-0 z-[1] max-h-[100%] min-h-[200px] rounded-t-lg p-4 text-base-txt-primary`}
            initial={disableAnimation ? { y: 0 } : { y: '100%' }}
            animate={{ y: 0 }}
            onAnimationComplete={() => {
              setStatus(id, 'opened');
            }}
            exit={{ y: '100%' }}
            transition={{ duration: TRANSITION_DURATION, ease: 'easeInOut' }}
            drag={draggable ? 'y' : false}
            dragControls={controls}
            dragDirectionLock
            dragElastic={0.5}
            dragMomentum={false}
            dragListener={false}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDrag={onDrag}
            style={{
              y,
              touchAction: 'pan-y',
              height: fullScreen ? '100%' : undefined,
              maxHeight: fullScreen ? undefined : maxHeight,
              borderRadius: fullScreen ? 0 : undefined,
              background: background || 'var(--semantic-global-dialog-bg-default)',
              backdropFilter: blur ? 'blur(32px)' : undefined,
              paddingBottom: 'calc(var(--navigation-bar-height) + 16px)',
              paddingTop: fullScreen ? `calc(var(--status-bar-height) + 16px)` : `${draggable ? `${DRAG_BAR_HEIGHT_REM}rem` : 0}`,
              overflowY: scrollable ? 'hidden' : 'auto',
              ...containerStyle,
              overscrollBehavior: 'none',
            }}
          >
            <div className={`content-container relative h-full min-h-0 w-full ${scrollable ? 'overflow-auto' : ''}`}>
              {draggable ? (
                <BottomSheetDragHandle
                  controls={controls}
                  y={y}
                  height={DRAG_HANDLE_HEIGHT}
                  dragThreshold={DRAG_THRESHOLD}
                  offsetY={typeof window !== 'undefined' ? window.innerHeight - sheetHeight : 0}
                />
              ) : null}
              {children}
              {showScrollBar ? (
                <ScrollBar
                  zIndex={zIndex}
                  y={y}
                  mountTo={portalRoot}
                  offsetY={typeof window !== 'undefined' ? window.innerHeight - sheetHeight : 0}
                  delay={TRANSITION_DURATION_MS}
                />
              ) : null}
              {showMyCoin ? (
                // background가 transparent 인 경우 content 와 너무 멀리 떨어져있음
                <MyCoinBadge y={y} mountTo={portalRoot} zIndex={zIndex ?? 102 + 1} offsetY={window.innerHeight - sheetHeight - (background === 'transparent' ? 20 : 64)} />
              ) : null}
              {draggable && !hideBar ? (
                <DragBar y={y} offsetY={typeof window !== 'undefined' ? window.innerHeight - sheetHeight : 0} zIndex={zIndex} mountTo={portalRoot} delay={TRANSITION_DURATION_MS} />
              ) : null}
            </div>
          </m.div>
        </div>
      </LazyMotion>
    </AnimatePresence>,
    portalRoot
  );
};
